/**
 * AiChatMentionPopup — Floating autocomplete for @-mentions of database objects and columns.
 */

import { useEffect, useRef } from 'react';
import { Table2, Eye, Workflow, FunctionSquare, Columns } from 'lucide-react';
import type { DatabaseObject } from '../../database-objects/models/databaseObjectTypes';
import type { DatabaseObjectType } from '../../database-objects/models/databaseObjectTypes';
import type { ColumnInfo } from '../../database-objects/services/columnsService';
import type { MentionMode } from '../hooks/useAtMention';

type AiChatMentionPopupProps = {
  objects: DatabaseObject[];
  columns: ColumnInfo[];
  mode: MentionMode;
  parentObjectName: string | null;
  selectedIndex: number;
  hasMore: boolean;
  remainingCount: number;
  columnsLoading?: boolean;
  onSelectObject: (obj: DatabaseObject) => void;
  onSelectColumn: (col: ColumnInfo) => void;
  /** Position relative to the textarea's top-left corner. */
  position: { top: number; left: number };
};

const TYPE_ICONS: Partial<Record<DatabaseObjectType, typeof Table2>> = {
  table: Table2,
  view: Eye,
  'stored-procedure': Workflow,
  function: FunctionSquare,
};

const TYPE_LABELS: Partial<Record<DatabaseObjectType, string>> = {
  table: 'Tables',
  view: 'Views',
  'stored-procedure': 'Procedures',
  function: 'Functions',
};

function ColumnItem({ col, isSelected, selectedRef, onClick }: {
  col: ColumnInfo;
  isSelected: boolean;
  selectedRef: React.RefObject<HTMLDivElement>;
  onClick: () => void;
}) {
  return (
    <div
      ref={isSelected ? selectedRef : undefined}
      className={`flex items-center gap-2 px-2 py-1 text-xs cursor-pointer ${
        isSelected ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50'
      }`}
      onClick={onClick}
    >
      <Columns className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      <span className="truncate">{col.name}</span>
      <span className="ml-auto text-[10px] text-muted-foreground truncate">{col.dataType}</span>
    </div>
  );
}

export function AiChatMentionPopup({
  objects,
  columns,
  mode,
  parentObjectName,
  selectedIndex,
  hasMore,
  remainingCount,
  columnsLoading = false,
  onSelectObject,
  onSelectColumn,
  position,
}: AiChatMentionPopupProps) {
  const selectedRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    selectedRef.current?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  const isEmpty = mode === 'columns'
    ? columns.length === 0 && !columnsLoading
    : mode === 'mixed'
      ? columns.length === 0 && objects.length === 0
      : objects.length === 0;

  const isLoadingColumns = mode === 'columns' && columns.length === 0 && columnsLoading;

  if (isLoadingColumns) {
    return (
      <div
        className="absolute z-50 w-64 bg-popover border border-border rounded-md shadow-md p-2 text-xs text-muted-foreground"
        style={{ bottom: `calc(100% - ${position.top}px + 4px)`, left: position.left }}
        data-testid="ai-chat-mention-popup"
      >
        Loading columns...
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div
        className="absolute z-50 w-64 bg-popover border border-border rounded-md shadow-md p-2 text-xs text-muted-foreground"
        style={{ bottom: `calc(100% - ${position.top}px + 4px)`, left: position.left }}
        data-testid="ai-chat-mention-popup"
      >
        {mode === 'columns'
          ? 'No columns found'
          : 'Loading database objects...'}
      </div>
    );
  }

  // Column-only mode (dot-chaining)
  if (mode === 'columns') {
    return (
      <div
        className="absolute z-50 w-64 max-h-60 overflow-y-auto bg-popover border border-border rounded-md shadow-md py-1"
        style={{ bottom: `calc(100% - ${position.top}px + 4px)`, left: position.left }}
        data-testid="ai-chat-mention-popup"
      >
        {parentObjectName && (
          <div className="px-2 py-1 text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
            Columns of {parentObjectName}
          </div>
        )}
        {columns.map((col, i) => (
          <ColumnItem
            key={col.name}
            col={col}
            isSelected={i === selectedIndex}
            selectedRef={selectedRef}
            onClick={() => onSelectColumn(col)}
          />
        ))}
        {hasMore && (
          <div className="px-2 py-1 text-[10px] text-muted-foreground text-center">
            ... and {remainingCount} more
          </div>
        )}
      </div>
    );
  }

  // Mixed mode (columns at top + objects below)
  if (mode === 'mixed') {
    let globalIndex = 0;
    let lastType: DatabaseObjectType | null = null;

    return (
      <div
        className="absolute z-50 w-64 max-h-60 overflow-y-auto bg-popover border border-border rounded-md shadow-md py-1"
        style={{ bottom: `calc(100% - ${position.top}px + 4px)`, left: position.left }}
        data-testid="ai-chat-mention-popup"
      >
        {/* Columns section */}
        {columns.length > 0 && (
          <>
            <div className="px-2 py-1 text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
              Columns of {parentObjectName}
            </div>
            {columns.map((col) => {
              const idx = globalIndex++;
              return (
                <ColumnItem
                  key={`col-${col.name}`}
                  col={col}
                  isSelected={idx === selectedIndex}
                  selectedRef={selectedRef}
                  onClick={() => onSelectColumn(col)}
                />
              );
            })}
          </>
        )}

        {/* Objects section */}
        {objects.map((obj) => {
          const idx = globalIndex++;
          const Icon = TYPE_ICONS[obj.type] ?? Table2;
          const showHeader = obj.type !== lastType;
          lastType = obj.type;
          const objKey = `${obj.type}-${obj.schema}-${obj.name}`;

          return (
            <div key={objKey}>
              {showHeader && (
                <div className="px-2 py-1 text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
                  {TYPE_LABELS[obj.type] ?? obj.type}
                </div>
              )}
              <div
                ref={idx === selectedIndex ? selectedRef : undefined}
                className={`flex items-center gap-2 px-2 py-1 text-xs cursor-pointer ${
                  idx === selectedIndex ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50'
                }`}
                onClick={() => onSelectObject(obj)}
                data-testid={`mention-item-${obj.schema}-${obj.name}`}
              >
                <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="truncate">
                  {obj.schema}.{obj.name}
                </span>
              </div>
            </div>
          );
        })}

        {hasMore && (
          <div className="px-2 py-1 text-[10px] text-muted-foreground text-center">
            ... and {remainingCount} more
          </div>
        )}
      </div>
    );
  }

  // Objects-only mode (default)
  let lastType: DatabaseObjectType | null = null;

  return (
    <div
      className="absolute z-50 w-64 max-h-60 overflow-y-auto bg-popover border border-border rounded-md shadow-md py-1"
      style={{ bottom: `calc(100% - ${position.top}px + 4px)`, left: position.left }}
      data-testid="ai-chat-mention-popup"
    >
      {objects.map((obj, i) => {
        const Icon = TYPE_ICONS[obj.type] ?? Table2;
        const showHeader = obj.type !== lastType;
        lastType = obj.type;
        const objKey = `${obj.type}-${obj.schema}-${obj.name}`;

        return (
          <div key={objKey}>
            {showHeader && (
              <div className="px-2 py-1 text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
                {TYPE_LABELS[obj.type] ?? obj.type}
              </div>
            )}
            <div
              ref={i === selectedIndex ? selectedRef : undefined}
              className={`flex items-center gap-2 px-2 py-1 text-xs cursor-pointer ${
                i === selectedIndex ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50'
              }`}
              onClick={() => onSelectObject(obj)}
              data-testid={`mention-item-${obj.schema}-${obj.name}`}
            >
              <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <span className="truncate">
                {obj.schema}.{obj.name}
              </span>
            </div>
          </div>
        );
      })}

      {hasMore && (
        <div className="px-2 py-1 text-[10px] text-muted-foreground text-center">
          ... and {remainingCount} more
        </div>
      )}
    </div>
  );
}
